"use client";

import "@styles/common/navigation-bar.css";
import { Button } from "../ui/button";
import Link from "next/link";
import clsx from "clsx";
import { useRef } from "react";
import gsap from "gsap";
import useNavigationStore from "@lib/stores/useNavigationStore";
import useIsAtViewportTop from "@hooks/useIsAtViewportTop";
import { useGSAP } from "@gsap/react";
import MenuButton from "./menu-button";
import { usePathname } from "next/navigation";
import TransitionLink from "./transition-link-button";
import { ScrollProgress } from "../magicui/scroll-progress";
import Image from "next/image";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Element } from "react-scroll";

export type LinkType = {
  label: string;
  link: string;
};

export const navLinks: LinkType[] = [
  {
    label: "Projects",
    link: "/projects",
  },
  {
    label: "Services",
    link: "/services",
  },
  {
    label: "About us",
    link: "/about-us",
  },
  {
    label: "Contact us",
    link: "/contact-us",
  },
];

function NavigationBar({ url }: Readonly<{ url: string }>) {
  const { isMenuOpen } = useNavigationStore();
  const navRef = useRef(null);
  const navIsAtTop = useIsAtViewportTop(navRef, { offset: 0 });
  const pathname = usePathname();
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useGSAP(
    () => {
      gsap.fromTo(
        "#navContents",
        { opacity: 0, y: -50 },
        { opacity: 1, y: 0, delay: 1 },
      );
    },
    { scope: navRef, dependencies: [pathname] },
  );

  return (
    <nav
      ref={navRef}
      className={clsx(
        isMenuOpen ? "fixed bg-transparent" : "sticky",
        navIsAtTop ? "visible" : "invisible",
        navIsAtTop && !isMenuOpen && "bg-white shadow-md",
        "left-0 top-0 z-40 w-full px-5 py-2 font-Satoshi-Black transition lg:visible lg:my-3 lg:block lg:p-5",
      )}
    >
      <div
        id="navContents"
        className="mx-auto flex max-w-7xl items-center justify-between opacity-0 lg:justify-around"
      >
        <Element name="brandIcon" className="z-10">
          <TransitionLink
            label={!isDesktop ? "RAKE" : null}
            href="/"
            className={clsx(
              isMenuOpen ? "text-white" : "text-black",
              "gap-0 text-2xl font-bold uppercase tracking-widest transition duration-700 lg:h-full lg:text-black",
            )}
          >
            <Image
              src={"/rake-con-favicon.png"}
              alt="Rake Logo"
              width={40}
              height={50}
              loading="lazy"
            />
            <span>AKE</span>
          </TransitionLink>
        </Element>
        <div className="hidden justify-center gap-5 lg:flex">
          {navLinks.map(({ label, link }) => (
            <TransitionLink
              key={label}
              label={label}
              href={link}
              className="text-base xl:text-lg"
            />
          ))}
        </div>
        <div className="hidden lg:inline-block">
          <Button
            asChild
            className={clsx(
              navIsAtTop ? "bg-orange-600 hover:bg-orange-700" : "bg-slate-800",
              "rounded-sm p-6 text-sm font-normal",
            )}
          >
            <Link target="_blank" rel="noopener noreferrer" href={url}>
              Book an Online Meeting
            </Link>
          </Button>
        </div>
        <MenuButton />
      </div>
      {navIsAtTop && !isMenuOpen && (
        <ScrollProgress className="top-[56px] lg:top-[101.406px]" />
      )}
    </nav>
  );
}

export default NavigationBar;
